import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { LogOut, Trash2, Mail, Phone, Calendar, Filter, ExternalLink, RefreshCw, Inbox, CalendarClock } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const STATUS_COLORS = {
  new: "border-neon-cyan/40 text-neon-cyan bg-neon-cyan/5",
  contacted: "border-neon-orange/40 text-neon-orange bg-neon-orange/5",
  closed: "border-neon-lime/40 text-neon-lime bg-neon-lime/5",
  scheduled: "border-neon-lime/40 text-neon-lime bg-neon-lime/5",
  completed: "border-neon-cyan/40 text-neon-cyan bg-neon-cyan/5",
  cancelled: "border-red-500/40 text-red-400 bg-red-500/5",
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("leads");
  const [leads, setLeads] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState({ total_leads: 0, new_leads: 0, by_service: [] });
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const adminEmail = localStorage.getItem("tsv_admin_email") || "admin";

  const load = async () => {
    setLoading(true);
    try {
      const [leadsRes, statsRes, bookingsRes] = await Promise.all([
        api.get("/admin/leads"),
        api.get("/admin/stats"),
        api.get("/admin/bookings"),
      ]);
      setLeads(leadsRes.data);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("tsv_token");
        navigate("/admin/login");
        return;
      }
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem("tsv_token")) {
      navigate("/admin/login");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { setFilter("all"); }, [tab]);

  const logout = () => {
    localStorage.removeItem("tsv_token");
    localStorage.removeItem("tsv_admin_email");
    navigate("/admin/login");
  };

  const updateLeadStatus = async (id, status) => {
    try {
      await api.patch(`/admin/leads/${id}`, { status });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
      toast.success("Status updated");
    } catch { toast.error("Update failed"); }
  };
  const updateBookingStatus = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      setBookings((bs) => bs.map((b) => (b.id === id ? { ...b, status } : b)));
      toast.success("Booking updated");
    } catch { toast.error("Update failed"); }
  };

  const removeLead = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    try {
      await api.delete(`/admin/leads/${id}`);
      setLeads((ls) => ls.filter((l) => l.id !== id));
      toast.success("Lead deleted");
    } catch { toast.error("Delete failed"); }
  };
  const removeBooking = async (id) => {
    if (!window.confirm("Delete this booking permanently?")) return;
    try {
      await api.delete(`/admin/bookings/${id}`);
      setBookings((bs) => bs.filter((b) => b.id !== id));
      toast.success("Booking deleted");
    } catch { toast.error("Delete failed"); }
  };

  const filteredLeads = filter === "all" ? leads : leads.filter((l) => l.status === filter);
  const filteredBookings = filter === "all" ? bookings : bookings.filter((b) => b.status === filter);

  return (
    <div data-testid="admin-dashboard-page" className="min-h-screen relative">
      <div className="absolute inset-0 grid-bg opacity-10 pointer-events-none" />

      {/* Header */}
      <header className="border-b border-white/10 bg-black/40 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <span className="block w-2 h-2 bg-neon-cyan rounded-full" />
            <span className="font-heading font-black text-base">
              TECHSAVVY<span className="text-neon-orange">PRENEURS</span>{" "}
              <span className="font-mono text-xs uppercase tracking-widest text-white/40 ml-2">// ADMIN</span>
            </span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="hidden md:block font-mono text-xs uppercase tracking-widest text-white/40">
              {adminEmail}
            </span>
            <button onClick={load} data-testid="admin-refresh" className="btn-ghost !py-2 !px-3" title="Refresh">
              <RefreshCw size={14} />
            </button>
            <button onClick={logout} data-testid="admin-logout" className="btn-ghost !py-2 !px-4">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-12 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 mb-10">
          <StatBox label="Total Leads" value={stats.total_leads} accent="cyan" />
          <StatBox label="New Leads" value={stats.new_leads} accent="orange" />
          <StatBox label="Bookings" value={bookings.length} accent="lime" />
          <StatBox label="Scheduled" value={bookings.filter(b => b.status === "scheduled").length} accent="pink" />
        </div>

        {/* Tabs */}
        <div className="flex gap-px bg-white/10 mb-6 w-full md:w-fit">
          <button
            onClick={() => setTab("leads")}
            data-testid="tab-leads"
            className={`flex items-center gap-2 px-6 py-3 font-mono text-xs uppercase tracking-widest transition-all ${
              tab === "leads" ? "bg-[#0F0F16] text-neon-cyan" : "bg-[#05050A] text-white/40 hover:text-white"
            }`}
          >
            <Inbox size={13} /> Leads ({leads.length})
          </button>
          <button
            onClick={() => setTab("bookings")}
            data-testid="tab-bookings"
            className={`flex items-center gap-2 px-6 py-3 font-mono text-xs uppercase tracking-widest transition-all ${
              tab === "bookings" ? "bg-[#0F0F16] text-neon-lime" : "bg-[#05050A] text-white/40 hover:text-white"
            }`}
          >
            <CalendarClock size={13} /> Calls ({bookings.length})
          </button>
        </div>

        {/* Filters */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter size={14} className="text-white/40" />
            {(tab === "leads" ? ["all", "new", "contacted", "closed"] : ["all", "scheduled", "completed", "cancelled"]).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                data-testid={`filter-${f}`}
                className={`font-mono text-[11px] uppercase tracking-widest px-3 py-1.5 border transition-all ${
                  filter === f
                    ? "border-neon-cyan text-neon-cyan bg-neon-cyan/5"
                    : "border-white/10 text-white/50 hover:text-white hover:border-white/30"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="font-mono text-xs uppercase tracking-widest text-white/40">
            {(tab === "leads" ? filteredLeads : filteredBookings).length}{" "}
            {(tab === "leads" ? filteredLeads : filteredBookings).length === 1 ? "result" : "results"}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="glass p-16 text-center text-white/40 font-mono text-sm">Loading…</div>
        ) : tab === "leads" ? (
          filteredLeads.length === 0 ? (
            <EmptyState icon={Mail} label="No leads in this view yet." testid="leads-empty" />
          ) : (
            <div className="space-y-3">
              {filteredLeads.map((lead) => (
                <div key={lead.id} data-testid={`lead-row-${lead.id}`} className="glass glass-hover p-5 md:p-6 grid md:grid-cols-12 gap-4 items-start">
                  <div className="md:col-span-3">
                    <div className="font-heading font-bold text-base">{lead.name}</div>
                    <div className="font-mono text-xs text-white/50 mt-1 flex items-center gap-1.5"><Mail size={11} />{lead.email}</div>
                    {lead.phone && <div className="font-mono text-xs text-white/50 mt-1 flex items-center gap-1.5"><Phone size={11} />{lead.phone}</div>}
                  </div>
                  <div className="md:col-span-2"><div className="overline mb-1">// Venture</div><div className="text-sm text-white/80">{lead.service}</div></div>
                  <div className="md:col-span-4"><div className="overline mb-1">// Message</div><div className="text-sm text-white/70 line-clamp-3">{lead.message}</div></div>
                  <div className="md:col-span-2">
                    <div className="overline mb-1">// Status</div>
                    <select value={lead.status} onChange={(e) => updateLeadStatus(lead.id, e.target.value)} data-testid={`lead-status-${lead.id}`} className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 border cursor-pointer ${STATUS_COLORS[lead.status] || STATUS_COLORS.new}`}>
                      <option value="new" className="bg-[#0F0F16]">New</option>
                      <option value="contacted" className="bg-[#0F0F16]">Contacted</option>
                      <option value="closed" className="bg-[#0F0F16]">Closed</option>
                    </select>
                    <div className="font-mono text-[10px] text-white/30 mt-2 flex items-center gap-1"><Calendar size={9} />{new Date(lead.created_at).toLocaleDateString()}</div>
                  </div>
                  <div className="md:col-span-1 flex md:justify-end gap-2">
                    <a href={`mailto:${lead.email}`} className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-neon-cyan hover:text-neon-cyan transition-all" title="Email"><ExternalLink size={13} /></a>
                    <button onClick={() => removeLead(lead.id)} data-testid={`lead-delete-${lead.id}`} className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-red-500 hover:text-red-500 transition-all" title="Delete"><Trash2 size={13} /></button>
                  </div>
                </div>
              ))}
            </div>
          )
        ) : filteredBookings.length === 0 ? (
          <EmptyState icon={CalendarClock} label="No calls booked yet." testid="bookings-empty" />
        ) : (
          <div className="space-y-3">
            {filteredBookings.map((b) => (
              <div key={b.id} data-testid={`booking-row-${b.id}`} className="glass glass-hover p-5 md:p-6 grid md:grid-cols-12 gap-4 items-start border-l-2 border-neon-lime/40">
                <div className="md:col-span-3">
                  <div className="font-heading font-bold text-base">{b.name}</div>
                  <div className="font-mono text-xs text-white/50 mt-1 flex items-center gap-1.5"><Mail size={11} />{b.email}</div>
                  {b.phone && <div className="font-mono text-xs text-white/50 mt-1 flex items-center gap-1.5"><Phone size={11} />{b.phone}</div>}
                </div>
                <div className="md:col-span-3">
                  <div className="overline mb-1">// When</div>
                  <div className="text-sm text-neon-lime font-semibold">{b.date}</div>
                  <div className="text-xs text-white/60 font-mono mt-0.5">{b.time_slot}</div>
                </div>
                <div className="md:col-span-3">
                  <div className="overline mb-1">// Focus</div>
                  <div className="text-sm text-white/80">{b.venture}</div>
                  {b.notes && <div className="text-xs text-white/50 mt-1 line-clamp-2">{b.notes}</div>}
                </div>
                <div className="md:col-span-2">
                  <div className="overline mb-1">// Status</div>
                  <select value={b.status} onChange={(e) => updateBookingStatus(b.id, e.target.value)} data-testid={`booking-status-${b.id}`} className={`text-xs font-mono uppercase tracking-widest px-3 py-1.5 border cursor-pointer ${STATUS_COLORS[b.status] || STATUS_COLORS.scheduled}`}>
                    <option value="scheduled" className="bg-[#0F0F16]">Scheduled</option>
                    <option value="completed" className="bg-[#0F0F16]">Completed</option>
                    <option value="cancelled" className="bg-[#0F0F16]">Cancelled</option>
                  </select>
                </div>
                <div className="md:col-span-1 flex md:justify-end gap-2">
                  <a href={`mailto:${b.email}`} className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-neon-cyan hover:text-neon-cyan transition-all" title="Email"><ExternalLink size={13} /></a>
                  <button onClick={() => removeBooking(b.id)} data-testid={`booking-delete-${b.id}`} className="w-9 h-9 flex items-center justify-center border border-white/10 hover:border-red-500 hover:text-red-500 transition-all" title="Delete"><Trash2 size={13} /></button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function StatBox({ label, value, accent }) {
  const colors = { cyan: "text-neon-cyan", orange: "text-neon-orange", lime: "text-neon-lime", pink: "text-neon-pink" };
  return (
    <div className="bg-[#05050A] p-6 md:p-8">
      <div className={`font-mono text-4xl md:text-5xl font-bold ${colors[accent] || "text-white"}`}>
        {String(value).padStart(2, "0")}
      </div>
      <div className="overline mt-2 text-white/40">// {label}</div>
    </div>
  );
}

function EmptyState({ icon: Icon, label, testid }) {
  return (
    <div data-testid={testid} className="glass p-16 text-center">
      <Icon size={32} className="mx-auto text-white/20 mb-4" />
      <p className="text-white/40 font-mono text-sm uppercase tracking-widest">{label}</p>
    </div>
  );
}
